import { Provider } from '@angular/core';
import {
  DASHBOARD_FINANCE_DEFAULT_TOKEN,
  DASHBOARD_FINANCE_TOKEN,
  FINANCIAL_STATISTIC,
} from '@celerofinancas/ui-reports';

/**
 * Presentation properties of the dashboard blocks, starting from the package's own and
 * changing only what the cards ask for.
 *
 * ## Why this lives in the root injector, and not on the screen
 *
 * It used to be provided by `DashboardComponent`, and half of it silently did nothing.
 *
 * `DashboardFinanceDataService` is `providedIn: 'root'` and reads this token from the root
 * injector. A provider on the component is below that, so the service never saw it and
 * fell back to the package defaults. The component itself did see it, which is what made
 * the failure so quiet: the titles and the legends followed our configuration while the
 * data the service built ignored it.
 *
 * That is exactly what happened with `showPredictedData`: the charts dropped "Previstos"
 * from the legend and kept drawing the predicted bars. Only the screenshot showed it.
 */
const STATISTIC_PROPERTIES = new Map(DASHBOARD_FINANCE_DEFAULT_TOKEN.statisticProperties);

/**
 * The "see more" link calls `onCardRedirect`, which we do not configure. Without that
 * destination screen the link would be visible and lead nowhere, a dead end in front of
 * the client.
 */
for (const [statistic, properties] of STATISTIC_PROPERTIES) {
  if (properties?.showLinkButton) {
    STATISTIC_PROPERTIES.set(statistic, { ...properties, showLinkButton: false });
  }
}

/**
 * Both charts drop their predicted series, which is what the reordering design
 * ([86e33z2f1](https://app.clickup.com/t/86e33z2f1)) asks for when it labels them
 * "apenas com valores REALIZADOS".
 *
 * The tooltips come with them. The package's copy promises what the chart no longer shows
 * — "Valores realizados (últimos 15 dias) e previstos (para 15 dias)" on the line, and
 * "com previsão" on the bars — and a tooltip describing a series that is not on screen is
 * worse than no tooltip.
 */
STATISTIC_PROPERTIES.set(FINANCIAL_STATISTIC.lineDailyBalance, {
  ...STATISTIC_PROPERTIES.get(FINANCIAL_STATISTIC.lineDailyBalance),
  showPredictedData: false,
  tooltip: 'Valores realizados dos últimos 15 dias',
});

STATISTIC_PROPERTIES.set(FINANCIAL_STATISTIC.barMonthlyComparative, {
  ...STATISTIC_PROPERTIES.get(FINANCIAL_STATISTIC.barMonthlyComparative),
  showPredictedData: false,
  tooltip: 'Comparativo mensal de entradas e saídas realizadas dos últimos dois meses e do mês atual',
});

/**
 * Dashboard configuration, provided where the package's own service can reach it.
 */
export const DASHBOARD_TOKENS: Provider[] = [
  {
    provide: DASHBOARD_FINANCE_TOKEN,
    useValue: { statisticProperties: STATISTIC_PROPERTIES },
  },
];
